import React, {
  ReactElement,
  Dispatch,
  SetStateAction,
  useEffect,
} from 'react';
import YouTube from 'react-youtube';
import type YouTubePlayer from 'youtube-player';

import { Settings } from '../../../models';
import { getVideoId, getPx } from '../../../helpers';
import { SONGS } from '../../../examples';

interface PreviewPlayerProps {
  width: number;
  settings: Settings;
  showPreview: boolean;
  previewPlayer?: YouTubePlayer;
  setPreviewPlayer: Dispatch<SetStateAction<YouTubePlayer>>;
}

function PreviewPlayer({
  width,
  settings,
  showPreview,
  previewPlayer,
  setPreviewPlayer,
}: PreviewPlayerProps): ReactElement {
  const initVideoId = getVideoId(SONGS[0].source);

  // Control callbacks
  const onReady = (event) => setPreviewPlayer(event.target);
  const onPlay = (event) => {
    if (!showPreview) event.target.pauseVideo();
  };

  useEffect(() => {
    if (
      !showPreview &&
      previewPlayer?.getPlayerState() === YouTube.PlayerState.PLAYING
    )
      previewPlayer.pauseVideo();
  }, [showPreview]);
  useEffect(() => {
    const previewVideoId = getVideoId(
      settings.song.preview ?? settings.song.source
    );
    const currentVideoId = getVideoId(previewPlayer?.getVideoUrl());

    if (previewVideoId !== currentVideoId)
      previewPlayer?.cueVideoById(previewVideoId);
  }, [settings]);

  // Styling
  const safeWidth = Math.min(width, 480);
  const safeHeight = Math.min((width * 315) / 560, 270);
  const opts = {
    width: String(safeWidth),
    height: String(safeHeight),
    frameBorder: 0,
    allow: 'fullscreen; encrypted-media',
    playerVars: {
      fs: 0 as const,
      iv_load_policy: 3 as const,
      modestbranding: 1 as const,
      playsinline: 1 as const,
      enablejsapi: 1 as const,
    },
  };
  const previewPlayerStyle = {
    display: 'inline-block',
    width: getPx(safeWidth),
    height: getPx(safeHeight),
    borderRadius: '0.5em',
    marginBottom: '0.5em',
    overflow: 'hidden',
  };

  return (
    <div style={previewPlayerStyle}>
      <YouTube
        videoId={initVideoId}
        title="Video Background"
        opts={opts}
        onReady={onReady}
        onPlay={onPlay}
      />
    </div>
  );
}

export default PreviewPlayer;
